"use client";
import React from "react";
import Video from "next-video";
import { motion } from "framer-motion";
// Remove static imports - load dynamically
import SpotlightCard from "./SpotlightCard";
import Player from "./ReactPlayerAdapter";

// Lazy load video imports
const loadVideo = async (path: string) => {
  return (await import(`../../../videos/${path}`)).default;
};

const tutorials = [
  {
    title: "AI-Powered Code Reviews",
    desc: "Our RAG system performs dynamic code analysis to detect bugs, highlight performance issues, and suggest actionable improvements.",
    videoPath: "1748864361097.mp4",
  },
  {
    title: "Explore Your Repo with Ease",
    desc: "Code Explorer helps you navigate codebases intuitively, find files fast, and visualize project structure, as well as take actions such as:",
    features: ['Review File', 'Explain File', 'Document File', 'Generate Tests'],
    videoPath: "1755701238044.mp4",
  },
  {
    title: "Bring AI Reviews into Slack & Discord",
    desc: "Get instant notifications, summaries, and code insights directly in your team's chat tools via webhooks.",
    videoPath: "1749125172399.mp4",
  },
];

type Props = {
  src: any
  as: any
  className?: string
  threshold?: number
  rootMargin?: string
}

function AutoPlayVideo({
  src,
  as,
  className,
  threshold = 0.5, // Changed from 0.9 - start loading earlier
  rootMargin = '0px 0px -5% 0px', // Changed from -10% - more aggressive
}: Props) {
  const ref = React.useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = React.useState(false)

  React.useEffect(() => {
    const el = ref.current
    if (!el) return
    let raf: number | null = null
    const io = new IntersectionObserver(
      ([entry]) => {
        if (raf) cancelAnimationFrame(raf)
        raf = requestAnimationFrame(() => {
          setIsVisible(entry.isIntersecting)
        })
      },
      { threshold, rootMargin }
    )
    io.observe(el)
    return () => {
      io.disconnect()
      if (raf) cancelAnimationFrame(raf)
    }
  }, [threshold, rootMargin])

  return (
    <div ref={ref} className={className}>
      <Video
        as={as}
        src={src}
        // send playback state through data-* so TS stays chill
        data-playing={isVisible}
        data-loop={true}
        data-muted={true}
        playsInline
        disablePictureInPicture={true}
        // If TS yells about loop/muted here, keep them ONLY as data-* (adapter reads them)
        // loop
        // muted
      />
    </div>
  )
}

const TutorialSections = () => {
  return (
    <section id="tutorials" className="text-white">
      <div className="max-w-6xl mx-auto space-y-8">
        {tutorials.map((vid, i) => (
          <TutorialItem key={i} vid={vid} index={i} />
        ))}
      </div>
    </section>
  );
};

// Separate component for lazy video loading
function TutorialItem({ vid, index }: { vid: typeof tutorials[0], index: number }) {
  const [videoSrc, setVideoSrc] = React.useState<any>(null);
  
  React.useEffect(() => {
    // Load video when component mounts (lazy)
    import(`../../../videos/${vid.videoPath}`).then((module) => {
      setVideoSrc(module.default);
    });
  }, [vid.videoPath]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
    >
      <SpotlightCard className={`grid md:grid-cols-3 max-md:p-3 gap-6 items-start`}>
        <div
          className={`relative w-full rounded-2xl overflow-hidden shadow-lg max-md:col-span-3 md:col-span-2 row-start-1 ${
            index % 2 === 1 ? "max-md:col-start-1" : "md:col-start-2"
          }`}
        >
          {videoSrc ? (
            <AutoPlayVideo as={Player} src={videoSrc} />
          ) : (
            <div className="w-full aspect-video bg-gray-800 animate-pulse" />
          )}
        </div>

        {/* Text */}
        <div
          className={`flex flex-col justify-center space-y-6 max-md:col-span-3 md:col-span-1 max-md:pb-4 md:py-4 md:row-start-1 ${
            index % 2 === 1 ? "col-start-1 md:col-start-3 md:pr-2" : " col-start-1 md:pl-2"
          }`}
        >
          <h2 className="text-2xl md:text-4xl font-bold">{vid.title}</h2>
          <p className="text-gray-300 text-lg">{vid.desc}</p>
          <ul className="text-gray-300 text-lg">
            {vid.features?.map((feature, idx) => <li className="list-disc list-inside" key={idx}>{feature}</li>)}
          </ul>
        </div>
      </SpotlightCard>
    </motion.div>
  )
}

export default TutorialSections;
